// =====================================================
// actu.tsx — Composant principal
// Nouveautés :
//   ✅ Détection réseau (NetInfo) — banner offline persistant
//   ✅ Blocage actions si hors-ligne avec toast explicite
//   ✅ Cache posts/submissions/profil (AsyncStorage) — affichage immédiat au montage
//   ✅ CACHE-FIRST STRICT : si un cache existe au montage, aucun fetch réseau
//      automatique n'est déclenché. Le refresh ne se fait plus que sur :
//        - pull-to-refresh (onRefresh)
//        - bouton refresh manuel (handleManualRefresh)
//        - retour de connexion après coupure réseau
//        - changement de filtre (applyFilter)
//      Le cache reste affiché indéfiniment tant qu'aucun de ces événements
//      ne se produit, et n'est remplacé qu'après un fetch réseau réussi.
//   ✅ Tracking vues (track-view) — 1 seconde visible, fire & forget, JWT user
//   ✅ SharersModal sur les partages
//   ✅ LimitNotification 7s sur create-post 429
//   ✅ balanceBox → router.push('/recharge')
//   ✅ Fix clavier modal create-post (KeyboardAvoidingView + ScrollView)
//   ✅ Header auto-hide au scroll (cache en scrollant vers le bas,
//      réapparaît automatiquement à l'arrêt du scroll) + double-tap fallback
//   ✅ LogoutModal : flux mémorisation-avant-confirmation, navigation
//      unifiée dans onLogoutSuccess (plus de router.replace concurrent).
//      refreshTokenRef capte le refresh_token à chaque getValidToken()
//      réussi pour que LogoutModal puisse toujours proposer la
//      mémorisation du compte avec les données à jour.
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  Modal, Dimensions, Platform, Animated, RefreshControl,
  StatusBar, ActivityIndicator, TextInput, KeyboardAvoidingView,
} from 'react-native';
import { useRouter }              from 'expo-router';
import { Ionicons }               from '@expo/vector-icons';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient }         from 'expo-linear-gradient';
import AsyncStorage               from '@react-native-async-storage/async-storage';
import * as Haptics               from 'expo-haptics';
import * as ImagePicker           from 'expo-image-picker';
import NetInfo                    from '@react-native-community/netinfo';
import { useTranslation }         from 'react-i18next';

// ── Composants extraits ──────────────────────────────────────────────────────
import PostCard                   from '../../components/PostCard';
import SubmissionCard             from '../../components/SubmissionCard';
import TikTokVideoModal           from '../../components/TikTokVideoModal';
import AudioFeedModal             from '../../components/AudioFeedModal';
import LimitNotification          from '../../components/LimitNotification';
import { SafeImage }              from '../../components/MediaViewers';

// ── Composants existants ─────────────────────────────────────────────────────
import HarmoniaLogo               from '../../components/HarmoniaLogo';
import CommentsModal              from '../../components/CommentsModal';
import LikersModal                from '../../components/LikersModal';
import SharersModal                from '../../components/SharersModal';
import EditPostModal              from '../../components/EditPostModal';
import SavedPostsModal            from '../../components/SavedPostsModal';
import SearchModal                from '../../components/SearchModal';
import LogoutModal                from '../../components/LogoutModal';
import UserProfileView            from '../../components/UserProfileView';
import ShareButton                from '../../components/ShareButton';
import PushPromptModal            from '../../components/PushPromptModal';
import { initPush }               from '../../lib/push';

// ── Helpers partagés ─────────────────────────────────────────────────────────
import {
  BACKEND_URL, HOME_URL, PROFILE_URL, NATIVE,
  getValidToken, uploadToStorage,
  Post, Submission, FeedItem, UserProfile, MyProfile,
  VideoFeedItem, AudioFeedItem,
  FilterMode, GameType, Visibility, ToastType,
  formatTimeAgo, getInitials,
} from '../../lib/feedHelpers';
import { useScrollAutoHide } from '../../hooks/useScrollAutoHide';
// ✅ Correct (chemin actuel réel)
import { useTabBarVisibility } from '@/components/CustomTabBar';

const { width, height } = Dimensions.get('window');

const HEADER_HEIGHT       = 75;
const FILTER_OVERLAY_TOP  = Platform.OS === 'ios' ? 110 : 90;
const HEADER_PADDING_TOP  = Platform.OS === 'ios' ? 50  : 30;
const FEED_FIRST_LIMIT    = 5;
const FEED_NEXT_LIMIT     = 10;
const LOAD_MORE_THRESHOLD = 0.85;

// ── Clés cache AsyncStorage ──────────────────────────────────────────────────
const CACHE_POSTS_KEY   = 'harmonia_cache_posts_v1';
const CACHE_SUBS_KEY    = 'harmonia_cache_subs_v1';
const CACHE_PROFILE_KEY = 'harmonia_cache_myprofile_v1';

// ── Durée minimale de visibilité pour tracker une vue (ms) ──────────────────
const VIEW_THRESHOLD_MS = 1000;

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export default function ActuScreen() {
  const router = useRouter();
  const { t } = useTranslation();

  // ── Session ──────────────────────────────────────────────────────────────────
  const [userId,      setUserId]      = useState('');
  const [accessToken, setAccessToken] = useState('');
  const userIdRef = useRef('');
  const tokenRef  = useRef('');
  const filterRef = useRef<FilterMode>('all');
  // Capté à chaque getValidToken() réussi — permet à LogoutModal de proposer
  // la mémorisation du compte avec le refresh_token le plus à jour, sans
  // avoir à refaire un appel async au moment de l'ouverture du modal.
  const refreshTokenRef = useRef<string>('');

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [myProfile,   setMyProfile]   = useState<MyProfile | null>(null);

  // ── Feed ─────────────────────────────────────────────────────────────────────
  const [posts,        setPosts]        = useState<Post[]>([]);
  const [postsOffset,  setPostsOffset]  = useState(0);
  const [postsHasMore, setPostsHasMore] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const postsLoadingRef = useRef(false);

  const [submissions,  setSubmissions]  = useState<Submission[]>([]);
  const [subsOffset,   setSubsOffset]   = useState(0);
  const [subsHasMore,  setSubsHasMore]  = useState(true);
  const [loadingSubs,  setLoadingSubs]  = useState(false);
  const subsLoadingRef = useRef(false);

  // ── Réseau ───────────────────────────────────────────────────────────────────
  // isOnline : null = inconnu (premier rendu), true/false = état réseau confirmé
  const [isOnline, setIsOnline] = useState<boolean | null>(null);
  const isOnlineRef = useRef<boolean | null>(null);

  // ── Cache-first strict ──────────────────────────────────────────────────────
  // hasCacheRef : true si un cache exploitable (posts, submissions ou profil)
  // a été trouvé au montage. Dans ce cas, on n'écrase PAS l'affichage avec un
  // fetch réseau automatique — l'utilisateur ne rafraîchit qu'en tirant vers
  // le bas, via le bouton refresh, lors d'un changement de filtre, ou lors
  // d'un retour de connexion après coupure.
  const hasCacheRef = useRef(false);

  // ── Tracking vues ────────────────────────────────────────────────────────────
  // viewedPostIds : posts déjà trackés dans cette session (évite les doublons)
  const viewedPostIds = useRef<Set<string>>(new Set());
  // viewTimers : timers en cours (postId → TimeoutId)
  const viewTimers    = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Layout des posts dans le ScrollView : postId → { top, height }
  const postLayouts   = useRef<Map<string, { top: number; height: number }>>(new Map());
  // Offset de scroll courant
  const scrollOffsetY = useRef(0);
  // Hauteur visible du ScrollView
  const scrollHeight  = useRef(0);

  // ── UI ───────────────────────────────────────────────────────────────────────
  const [filterMode,   setFilterMode]   = useState<FilterMode>('all');
  const [showFilter,   setShowFilter]   = useState(false);
  const [refreshing,   setRefreshing]   = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [toast, setToast] = useState<{ type: ToastType; text: string } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((type: ToastType, text: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, text });
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // Toast "pas de connexion" — appelé par chaque action bloquée
  const showOfflineToast = useCallback(() => {
    showToast('error', t('actuScreen.toasts.noConnection'));
  }, [showToast, t]);

  const [confirmConfig, setConfirmConfig] = useState<{
    visible: boolean; title: string; message: string;
    confirmText: string; cancelText: string; isDestructive?: boolean; onConfirm: () => void;
  } | null>(null);
  const [adminStrikePostId, setAdminStrikePostId] = useState<string | null>(null);

  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postContent,    setPostContent]    = useState('');
  const [postVisibility, setPostVisibility] = useState<Visibility>('friends');
  const [postImageUrl,   setPostImageUrl]   = useState<string | null>(null);
  const [postVideoUrl,   setPostVideoUrl]   = useState<string | null>(null);
  const [creatingPost,   setCreatingPost]   = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const [pushDone,       setPushDone]      = useState(false);
  const [showPushBtn,    setShowPushBtn]    = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  // ── Modaux ──────────────────────────────────────────────────────────────────
  const [selectedPost,            setSelectedPost]            = useState<Post | null>(null);
  const [showCommentsModal,       setShowCommentsModal]       = useState(false);
  const [selectedPostForComments, setSelectedPostForComments] = useState<string | null>(null);
  const [showLikersModal,         setShowLikersModal]         = useState(false);
  const [selectedPostForLikers,   setSelectedPostForLikers]   = useState<string | null>(null);
  const [showSharersModal,        setShowSharersModal]        = useState(false);
  const [selectedPostForSharers,  setSelectedPostForSharers]  = useState<string | null>(null);
  const [showEditModal,           setShowEditModal]           = useState(false);
  const [selectedPostForEdit,     setSelectedPostForEdit]     = useState<{id: string; content: string} | null>(null);
  const [showSavedPostsModal,     setShowSavedPostsModal]     = useState(false);
  const [showSearchModal,         setShowSearchModal]         = useState(false);
  const [showLogoutModal,         setShowLogoutModal]         = useState(false);
  const [selectedAuthorId,        setSelectedAuthorId]        = useState<string | null>(null);
  const [showUserProfileModal,    setShowUserProfileModal]    = useState(false);

  // ── LimitNotification ───────────────────────────────────────────────────────
  const [limitNotif,        setLimitNotif]        = useState(false);
  const [limitNotifMessage, setLimitNotifMessage] = useState('');

  // ── Modaux vidéo / audio ────────────────────────────────────────────────────
  const [videoModal,      setVideoModal]      = useState(false);
  const [videoItems,      setVideoItems]      = useState<VideoFeedItem[]>([]);
  const [videoStartIndex, setVideoStartIndex] = useState(0);
  const [audioModal,      setAudioModal]      = useState(false);
  const [audioItems,      setAudioItems]      = useState<AudioFeedItem[]>([]);
  const [audioStartIndex, setAudioStartIndex] = useState(0);

  const openVideoModal = useCallback((items: VideoFeedItem[], si: number) => {
    setVideoItems(items); setVideoStartIndex(si); setVideoModal(true);
  }, []);
  const openAudioModal = useCallback((items: AudioFeedItem[], si: number) => {
    setAudioItems(items); setAudioStartIndex(si); setAudioModal(true);
  }, []);

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastTap,       setLastTap]       = useState<number | null>(null);
  const headerAnim = useRef(new Animated.Value(0)).current;

  // Hauteur réelle du header, mesurée via onLayout (HEADER_HEIGHT n'est qu'une estimation)
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState(HEADER_HEIGHT);
  const measuredHeaderHeightRef = useRef(HEADER_HEIGHT);

  const { setTabBarHidden } = useTabBarVisibility();

  const isPro   = myProfile?.role === 'userpro';
  const isAdmin = ['adminpro', 'supreme', 'admin'].includes(myProfile?.role ?? '');

  // ═══════════════════════════════════════════════════════════════════════════
  // RÉSEAU — NetInfo listener
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    // Vérification initiale
    NetInfo.fetch().then(state => {
      const online = state.isConnected === true && state.isInternetReachable !== false;
      isOnlineRef.current = online;
      setIsOnline(online);
    });

    // Listener en temps réel
    const unsubscribe = NetInfo.addEventListener(state => {
      const online = state.isConnected === true && state.isInternetReachable !== false;
      const wasOnline = isOnlineRef.current;
      isOnlineRef.current = online;
      setIsOnline(online);

      // Retour en ligne après une coupure → refresh silencieux
      if (!wasOnline && online) {
        const mode = filterRef.current;
        if (mode !== 'images' && mode !== 'videos' && mode !== 'music') loadPosts(true);
        if (mode !== 'posts') loadSubmissions(true);
        loadMyProfile();
      }
    });

    return () => unsubscribe();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CACHE — Chargement immédiat depuis AsyncStorage
  // ═══════════════════════════════════════════════════════════════════════════

  // Renvoie true si au moins une donnée exploitable (posts, submissions ou
  // profil) a été trouvée en cache — utilisé pour décider si le fetch réseau
  // automatique au montage doit être sauté (cache-first strict).
  const loadCache = useCallback(async (): Promise<boolean> => {
    let hasCache = false;
    try {
      const [rawPosts, rawSubs, rawProfile] = await Promise.all([
        AsyncStorage.getItem(CACHE_POSTS_KEY),
        AsyncStorage.getItem(CACHE_SUBS_KEY),
        AsyncStorage.getItem(CACHE_PROFILE_KEY),
      ]);
      if (rawPosts) {
        const cached: Post[] = JSON.parse(rawPosts);
        if (cached.length > 0) { setPosts(cached); hasCache = true; }
      }
      if (rawSubs) {
        const cached: Submission[] = JSON.parse(rawSubs);
        if (cached.length > 0) { setSubmissions(cached); hasCache = true; }
      }
      if (rawProfile) {
        const cached: MyProfile = JSON.parse(rawProfile);
        if (cached) { setMyProfile(cached); hasCache = true; }
      }
    } catch {
      // Cache corrompu → on ignore silencieusement
    }
    return hasCache;
  }, []);

  const saveCache = useCallback(async (newPosts: Post[], newSubs: Submission[]) => {
    try {
      await Promise.all([
        AsyncStorage.setItem(CACHE_POSTS_KEY, JSON.stringify(newPosts)),
        AsyncStorage.setItem(CACHE_SUBS_KEY,  JSON.stringify(newSubs)),
      ]);
    } catch {
      // Écriture cache échouée → on ignore silencieusement
    }
  }, []);

  const saveProfileCache = useCallback(async (profile: MyProfile) => {
    try {
      await AsyncStorage.setItem(CACHE_PROFILE_KEY, JSON.stringify(profile));
    } catch {
      // Écriture cache échouée → on ignore silencieusement
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // INIT SESSION
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    // Afficher le cache immédiatement, avant même la session
    loadCache().then(found => { hasCacheRef.current = found; });

    getValidToken().then(t => {
      if (!t) return;
      userIdRef.current = t.uid;
      tokenRef.current  = t.token;
      refreshTokenRef.current = t.refresh_token || '';
      setUserId(t.uid);
      setAccessToken(t.token);
    });
  }, []);

  useEffect(() => {
    if (!userId || !accessToken) return;
    // Cache-first strict : si un cache existait déjà au montage (posts,
    // submissions ou profil), on ne relance aucun fetch réseau automatique
    // ici. Le refresh ne se déclenche plus que sur pull-to-refresh, bouton
    // refresh manuel, changement de filtre, ou retour de connexion.
    if (!hasCacheRef.current) {
      loadPosts(true);
      loadSubmissions(true);
      loadMyProfile();
    }
  }, [userId, accessToken]);

  useEffect(() => {
    if (!userId) return;
    const getAuth = async () => {
      const t = await getValidToken();
      if (!t) return null;
      return { user_id: t.uid, access_token: t.token };
    };
    checkPushSilent(getAuth).then(already => {
      if (already) setPushDone(true);
      else setTimeout(() => setShowPushPrompt(true), 1500);
    });
  }, [userId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // TRACKING DES VUES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Appelé depuis onLayout de chaque PostCard wrapper.
   * Mémorise la position absolue du post dans le ScrollView.
   */
  const handlePostLayout = useCallback((postId: string, top: number, height: number) => {
    postLayouts.current.set(postId, { top, height });
  }, []);

  /**
   * Envoie track-view au backend (fire & forget — ne bloque jamais l'UI).
   * Utilise le JWT utilisateur pour respecter la RLS policy.
   */
  const sendTrackView = useCallback(async (postId: string) => {
    try {
      const session = await getValidToken();
      if (!session) return;
      await fetch(HOME_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'track-view',
          user_id:      session.uid,
          access_token: session.token,
          post_id:      postId,
        }),
      });
      // On ne lit pas la réponse — le backend est idempotent (23505 silencieux)
    } catch {
      // Pas de connexion → on ignore, la vue ne sera pas trackée
    }
  }, []);

  /**
   * Vérifie quels posts sont actuellement visibles dans le viewport
   * et démarre/annule les timers d'1 seconde en conséquence.
   * Appelé à chaque événement onScroll.
   */
  const checkVisiblePosts = useCallback(() => {
    const scrollTop    = scrollOffsetY.current;
    const viewportH    = scrollHeight.current;
    const scrollBottom = scrollTop + viewportH;

    postLayouts.current.forEach(({ top, height }, postId) => {
      // Post déjà tracké dans cette session → skip
      if (viewedPostIds.current.has(postId)) {
        if (viewTimers.current.has(postId)) {
          clearTimeout(viewTimers.current.get(postId)!);
          viewTimers.current.delete(postId);
        }
        return;
      }

      const postBottom  = top + height;
      // On considère le post visible si au moins 50% est dans le viewport
      const visibleTop  = Math.max(top, scrollTop);
      const visibleBot  = Math.min(postBottom, scrollBottom);
      const visibleH    = visibleBot - visibleTop;
      const isVisible   = visibleH >= height * 0.5;

      if (isVisible) {
        // Démarrer le timer si pas encore démarré
        if (!viewTimers.current.has(postId)) {
          const timer = setTimeout(() => {
            viewedPostIds.current.add(postId);
            viewTimers.current.delete(postId);
            // Fire & forget — ne bloque pas si offline
            if (isOnlineRef.current) sendTrackView(postId);
          }, VIEW_THRESHOLD_MS);
          viewTimers.current.set(postId, timer);
        }
      } else {
        // Post sorti du viewport → annuler le timer
        if (viewTimers.current.has(postId)) {
          clearTimeout(viewTimers.current.get(postId)!);
          viewTimers.current.delete(postId);
        }
      }
    });
  }, [sendTrackView]);

  // Nettoyage des timers au démontage
  useEffect(() => {
    return () => {
      viewTimers.current.forEach(timer => clearTimeout(timer));
      viewTimers.current.clear();
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // PUSH
  // ═══════════════════════════════════════════════════════════════════════════

  const checkPushSilent = async (getAuthFn: () => Promise<any>): Promise<boolean> => {
    try {
      const auth = await getAuthFn();
      if (!auth) return true;
      if (Platform.OS === 'web') {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return true;
        const reg = await navigator.serviceWorker.getRegistration('/service-worker.js').catch(() => null);
        if (reg) { const existing = await reg.pushManager.getSubscription(); if (existing) return true; }
      } else {
        const cached = await AsyncStorage.getItem('harmonia_push_mobile_registered');
        if (cached === 'true') return true;
      }
      const platform = Platform.OS === 'web' ? 'web' : 'mobile';
      const res = await fetch(`${BACKEND_URL}/push`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'checkSubscription', platform, ...auth }),
      }).catch(() => null);
      if (res?.ok) { const data = await res.json(); return data.subscribed === true; }
      return false;
    } catch { return false; }
  };

  const handleEnablePush = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setShowPushPrompt(false); setShowPushBtn(false);
    const getAuthFn = async () => {
      const t = await getValidToken();
      if (!t) return null;
      return { user_id: t.uid, access_token: t.token };
    };
    try { await initPush(getAuthFn); setPushDone(true); } catch { setPushDone(true); }
  };
  const handleDismissPrompt = () => { setShowPushPrompt(false); setShowPushBtn(true); };

  // ═══════════════════════════════════════════════════════════════════════════
  // LOAD DATA
  // ═══════════════════════════════════════════════════════════════════════════

  const loadMyProfile = useCallback(async () => {
    try {
      const session = await getValidToken(); if (!session) return;
      refreshTokenRef.current = session.refresh_token || '';
      const res  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-profile', access_token: session.token }),
      });
      const data = await res.json();
      if (data.profile) {
        setMyProfile(data.profile);
        saveProfileCache(data.profile);
      }
    } catch (err) { console.error('[loadMyProfile]', err); }
  }, [saveProfileCache]);

  const loadPosts = useCallback(async (reset = false) => {
    if (postsLoadingRef.current) return;
    postsLoadingRef.current = true;
    setLoadingPosts(true);
    try {
      const session = await getValidToken(); if (!session) return;
      tokenRef.current  = session.token;
      userIdRef.current = session.uid;
      refreshTokenRef.current = session.refresh_token || '';
      const currentOffset = reset ? 0 : postsOffset;
      const limit         = currentOffset === 0 ? FEED_FIRST_LIMIT : FEED_NEXT_LIMIT;
      const res = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-feed', user_id: session.uid, access_token: session.token,
          limit, offset: currentOffset,
        }),
      });
      if (res.status === 401) { await AsyncStorage.removeItem('harmonia_session'); router.replace('/login'); return; }
      const data = await res.json();
      if (!data.success) return;
      const newPosts = (data.posts || []).map((p: any) => ({ ...p, item_type: 'post' }));

      if (reset) {
        setPosts(newPosts);
        setPostsOffset(data.next_offset ?? newPosts.length);
        // Mettre à jour le cache avec les nouvelles données fraîches
        setSubmissions(prev => { saveCache(newPosts, prev); return prev; });
      } else {
        setPosts(prev => {
          const existingIds = new Set(prev.map((p: Post) => p.id));
          const merged = [...prev, ...newPosts.filter((p: Post) => !existingIds.has(p.id))];
          // Cache mis à jour avec le feed complet fusionné
          setSubmissions(subs => { saveCache(merged, subs); return subs; });
          return merged;
        });
        setPostsOffset(data.next_offset ?? currentOffset + newPosts.length);
      }
      setPostsHasMore(data.has_more ?? false);
      if (reset && data.profile) {
        setUserProfile(data.profile);
        if (data.profile.role) setMyProfile(prev => ({ ...prev, role: data.profile.role } as MyProfile));
      }
    } catch (err) {
      console.error('[loadPosts]', err);
      // Erreur réseau → on garde le cache tel quel, pas de message d'erreur
    }
    finally { setLoadingPosts(false); postsLoadingRef.current = false; }
  }, [postsOffset, saveCache]);

  const loadSubmissions = useCallback(async (reset = false) => {
    if (subsLoadingRef.current) return;
    subsLoadingRef.current = true;
    setLoadingSubs(true);
    try {
      const session = await getValidToken(); if (!session) return;
      refreshTokenRef.current = session.refresh_token || '';
      const currentOffset = reset ? 0 : subsOffset;
      const limit         = currentOffset === 0 ? FEED_FIRST_LIMIT : FEED_NEXT_LIMIT;
      const res = await fetch(`${BACKEND_URL}/feed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          function: 'getSubmissions', user_id: session.uid, access_token: session.token,
          refresh_token: session.refresh_token, expires_at: session.expires_at,
          limit, offset: currentOffset,
        }),
      });
      if (res.status === 401) { await AsyncStorage.removeItem('harmonia_session'); router.replace('/login'); return; }
      const data = await res.json();
      if (!data.success) return;
      const newSubs = (data.submissions || []).map((s: any) => ({ ...s, item_type: 'submission' }));

      if (reset) {
        setSubmissions(newSubs);
        setSubsOffset(data.next_offset ?? newSubs.length);
        // Mettre à jour le cache avec les nouvelles données fraîches
        setPosts(prev => { saveCache(prev, newSubs); return prev; });
      } else {
        setSubmissions(prev => {
          const existingIds = new Set(prev.map((s: Submission) => s.id));
          const merged = [...prev, ...newSubs.filter((s: Submission) => !existingIds.has(s.id))];
          setPosts(p => { saveCache(p, merged); return p; });
          return merged;
        });
        setSubsOffset(data.next_offset ?? currentOffset + newSubs.length);
      }
      setSubsHasMore(data.has_more ?? false);
      if (data.new_token) {
        const raw = await AsyncStorage.getItem('harmonia_session');
        if (raw) await AsyncStorage.setItem('harmonia_session', JSON.stringify({ ...JSON.parse(raw), ...data.new_token }));
      }
    } catch (err) {
      console.error('[loadSubmissions]', err);
      // Erreur réseau → on garde le cache tel quel
    }
    finally { setLoadingSubs(false); subsLoadingRef.current = false; }
  }, [subsOffset, saveCache]);

  // ═══════════════════════════════════════════════════════════════════════════
  // REFRESH
  // ═══════════════════════════════════════════════════════════════════════════

  const onRefresh = async () => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    setRefreshing(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const mode = filterRef.current;
    if (mode !== 'images' && mode !== 'videos' && mode !== 'music') await loadPosts(true);
    if (mode !== 'posts') await loadSubmissions(true);
    await loadMyProfile();
    setRefreshing(false);
  };

  const handleManualRefresh = async () => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (isRefreshing) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setIsRefreshing(true);
    const mode = filterRef.current;
    if (mode !== 'images' && mode !== 'videos' && mode !== 'music') await loadPosts(true);
    if (mode !== 'posts') await loadSubmissions(true);
    await loadMyProfile();
    setIsRefreshing(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HEADER — Show / Hide (utilisé par scroll auto-hide ET double-tap manuel)
  // ═══════════════════════════════════════════════════════════════════════════

  const showHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (!prevVisible) {
        Animated.spring(headerAnim, {
          toValue: 0,
          useNativeDriver: true, tension: 80, friction: 10,
        }).start();
      }
      return true;
    });
  }, [headerAnim]);

  const hideHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (prevVisible) {
        Animated.spring(headerAnim, {
          // On utilise la hauteur réellement mesurée (+ une marge de sécurité)
          // pour être sûr que le header sorte complètement de l'écran.
          toValue: -(measuredHeaderHeightRef.current + 20),
          useNativeDriver: true, tension: 80, friction: 10,
        }).start();
      }
      return false;
    });
  }, [headerAnim]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SCROLL — Header local + tab bar globale (synchronisés) + Load more + Vues
  // ═══════════════════════════════════════════════════════════════════════════

  // onScrollExtra reprend l'ancienne logique de handleScroll : tracking des
  // vues de posts + pagination "load more". Le hook s'occupe désormais du
  // show/hide synchronisé du header ET de la tab bar du bas.
  const handleScrollExtra = useCallback((e: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;

    // Mémoriser position pour le tracking de vues
    scrollOffsetY.current = contentOffset.y;
    scrollHeight.current  = layoutMeasurement.height;

    checkVisiblePosts();

    // Load more
    const ratio = (contentOffset.y + layoutMeasurement.height) / contentSize.height;
    if (ratio < LOAD_MORE_THRESHOLD) return;
    const mode = filterRef.current;
    if (mode !== 'images' && mode !== 'videos' && mode !== 'music') {
      if (postsHasMore && !postsLoadingRef.current) loadPosts(false);
    }
    if (mode !== 'posts') {
      if (subsHasMore && !subsLoadingRef.current) loadSubmissions(false);
    }
  }, [postsHasMore, subsHasMore, loadPosts, loadSubmissions, checkVisiblePosts]);

  const { handleScroll } = useScrollAutoHide({
    onShow: showHeader,
    onHide: hideHeader,
    onScrollExtra: handleScrollExtra,
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // HEADER — Double-tap manuel (fallback conservé)
  // ═══════════════════════════════════════════════════════════════════════════

  const handleDoubleTap = () => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleHeader();
    else setLastTap(now);
  };

  const toggleHeader = () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (headerVisible) { hideHeader(); setTabBarHidden(true); }
    else                { showHeader(); setTabBarHidden(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FILTRE
  // ═══════════════════════════════════════════════════════════════════════════

  const applyFilter = async (mode: FilterMode) => {
    if (!isOnlineRef.current) { showOfflineToast(); setShowFilter(false); return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    filterRef.current = mode; setFilterMode(mode); setShowFilter(false);
    if (mode === 'posts' || mode === 'all') await loadPosts(true);
    if (mode !== 'posts') await loadSubmissions(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MEDIA UPLOAD
  // ═══════════════════════════════════════════════════════════════════════════

  const handlePickMedia = async (mediaType: 'image' | 'video') => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') { showToast('error', t('actuScreen.toasts.mediaAccessRequired')); return; }
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: mediaType === 'video' ? ImagePicker.MediaTypeOptions.Videos : ImagePicker.MediaTypeOptions.Images,
      allowsEditing: mediaType === 'image', quality: 0.8,
    });
    if (result.canceled || !result.assets[0]) return;
    setUploadingMedia(true);
    try {
      const session = await getValidToken(); if (!session) return;
      const context = mediaType === 'image' ? 'image' : 'video';
      const urlRes  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-upload-url', access_token: session.token, context }),
      });
      const urlData = await urlRes.json();
      if (!urlRes.ok) throw new Error(urlData.error);
      await uploadToStorage(urlData.signed_url, result.assets[0].uri);
      if (mediaType === 'image') setPostImageUrl(urlData.public_url);
      else                       setPostVideoUrl(urlData.public_url);
      showToast('success', mediaType === 'image' ? t('actuScreen.toasts.imageAdded') : t('actuScreen.toasts.videoAdded'));
    } catch (err: any) {
      showToast('error', err.message || t('actuScreen.toasts.cannotAddMedia'));
    } finally { setUploadingMedia(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CREATE POST
  // ═══════════════════════════════════════════════════════════════════════════

  const handleCreatePost = async () => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (!postContent.trim()) { showToast('error', t('actuScreen.toasts.contentRequired')); return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setCreatingPost(true);
    try {
      const session = await getValidToken(); if (!session) return;
      const res = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'create-post',
          access_token: session.token,
          content:      postContent.trim(),
          visibility:   postVisibility,
          imagepots:    postImageUrl || undefined,
          vidposts:     postVideoUrl || undefined,
        }),
      });
      const data = await res.json();

      if (res.status === 429) {
        setShowCreatePost(false);
        setPostContent('');
        setLimitNotifMessage(data.error || t('actuScreen.toasts.limitReached'));
        setLimitNotif(true);
        return;
      }

      if (!res.ok) throw new Error(data.error);
      setPostContent(''); setPostVisibility('friends');
      setPostImageUrl(null); setPostVideoUrl(null);
      setShowCreatePost(false);
      await loadPosts(true);
      showToast('success', t('actuScreen.toasts.postPublished'));
    } catch (err: any) {
      showToast('error', err.message || t('actuScreen.toasts.cannotPublish'));
    } finally { setCreatingPost(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SIGNALEMENT & ADMIN
  // ═══════════════════════════════════════════════════════════════════════════

  const handleReportPost = useCallback((postId: string) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    setConfirmConfig({
      visible: true,
      title: t('actuScreen.report.title'), message: t('actuScreen.report.message'),
      confirmText: t('actuScreen.report.confirm'), cancelText: t('actuScreen.report.cancel'), isDestructive: true,
      onConfirm: async () => {
        try {
          const session = await getValidToken(); if (!session) return;
          const res = await fetch(HOME_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'lock-post', user_id: session.uid, access_token: session.token, post_id: postId }),
          });
          const data = await res.json();
          if (data.success) showToast('success', t('actuScreen.toasts.reportRegistered'));
          else showToast('error', data.error || t('actuScreen.toasts.reportError'));
        } catch { showToast('error', t('actuScreen.toasts.networkError')); }
      },
    });
  }, [showOfflineToast, t]);

  const handleAdminStrikeAction = useCallback((postId: string) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    setAdminStrikePostId(postId);
  }, [showOfflineToast]);

  const executeAdminStrike = async (postId: string, status: 'pending' | 'active') => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    try {
      const session = await getValidToken(); if (!session) return;
      const res = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'admin-lock-status', user_id: session.uid, access_token: session.token,
          post_id: postId, status,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', t('actuScreen.toasts.postSanctioned', { status }));
        if (status === 'pending') setPosts(prev => prev.filter(p => p.id !== postId));
      } else { showToast('error', data.error || t('actuScreen.toasts.adminError')); }
    } catch { showToast('error', t('actuScreen.toasts.networkError')); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LIKE / SHARE / SAVE / VOTE
  // ═══════════════════════════════════════════════════════════════════════════

  const handleLike = useCallback(async (postId: string, liked: boolean) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    const session = await getValidToken(); if (!session) return;
    const res = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: liked ? 'like-post' : 'unlike-post', user_id: session.uid, access_token: session.token, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: { ...p.reactions, likes: data.likes }, user_liked: liked } : p
    ));
  }, [showOfflineToast]);

  const handleShare = useCallback(async (postId: string, shared: boolean) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    const session = await getValidToken(); if (!session) return;
    const res = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: shared ? 'share-post' : 'unshare-post', user_id: session.uid, access_token: session.token, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: { ...p.reactions, shares: data.shares }, user_shared: shared } : p
    ));
  }, [showOfflineToast]);

  const handleSave = useCallback(async (postId: string, saved: boolean) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    const session = await getValidToken(); if (!session) return;
    const res = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: saved ? 'save-post' : 'unsave-post', user_id: session.uid, access_token: session.token, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, user_saved: saved } : p
    ));
  }, [showOfflineToast]);

  const handleVote = useCallback(async (
    gameType: GameType, runId: string, voteForUserId: string,
    sessionId: string, currentlyVoted: boolean
  ) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    try {
      const session = await getValidToken(); if (!session) return;
      const res = await fetch(`${BACKEND_URL}/feed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          function:         currentlyVoted ? 'unvoteSubmission' : 'voteSubmission',
          user_id:          session.uid,
          access_token:     session.token,
          refresh_token:    session.refresh_token,
          expires_at:       session.expires_at,
          game_type:        gameType,
          run_id:           runId,
          vote_for_user_id: voteForUserId,
          session_id:       sessionId,
        }),
      });
      if (res.status === 401) { await AsyncStorage.removeItem('harmonia_session'); router.replace('/login'); return; }
      const data = await res.json();
      if (data.new_token) {
        const raw = await AsyncStorage.getItem('harmonia_session');
        if (raw) await AsyncStorage.setItem('harmonia_session', JSON.stringify({ ...JSON.parse(raw), ...data.new_token }));
      }
      if (!data.success) throw new Error(data.error);
    } catch (err) { console.error('[handleVote]', err); }
  }, [showOfflineToast]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS MODAUX
  // ═══════════════════════════════════════════════════════════════════════════

  const handleOpenComments  = useCallback((postId: string) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedPostForComments(postId); setShowCommentsModal(true);
  }, [showOfflineToast]);

  const handleOpenLikers    = useCallback((postId: string) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    setSelectedPostForLikers(postId); setShowLikersModal(true);
  }, [showOfflineToast]);

  const handleOpenSharers   = useCallback((postId: string) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    setSelectedPostForSharers(postId); setShowSharersModal(true);
  }, [showOfflineToast]);

  const handleOpenEdit      = useCallback((post: { id: string; content: string }) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    setSelectedPostForEdit(post); setShowEditModal(true);
  }, [showOfflineToast]);

  const handleLongPress     = useCallback((post: Post) => { setSelectedPost(post); }, []);

  const handleAuthorPress   = useCallback((authorId: string) => {
    if (!isOnlineRef.current) { showOfflineToast(); return; }
    if (!authorId || authorId === userId) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedAuthorId(authorId); setShowUserProfileModal(true);
  }, [userId, showOfflineToast]);

  // ── Déconnexion ────────────────────────────────────────────────────────────
  // LogoutModal ne navigue plus lui-même : il fait tout le travail de données
  // (appel API /logout, sauvegarde éventuelle du compte, nettoyage de la
  // session) puis remonte un seul résultat via onLogoutSuccess(wantsToRemember).
  // C'est ICI, et seulement ici, que la navigation finale est décidée — pour
  // éviter deux router.replace concurrents qui s'écrasaient l'un l'autre.
  const handleLogoutSuccess = useCallback((wantsToRemember: boolean) => {
    if (wantsToRemember) {
      router.replace('/saved-accounts');
    } else {
      router.replace('/login');
    }
  }, [router]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FEED CALCULÉ
  // ═══════════════════════════════════════════════════════════════════════════

  const dedup = (items: FeedItem[]): FeedItem[] => {
    const seen = new Map<string, FeedItem>();
    for (const item of items) {
      const k = `${item.item_type}-${item.id}`;
      if (!seen.has(k)) seen.set(k, item);
    }
    return Array.from(seen.values());
  };

  const displayedItems: FeedItem[] = (() => {
    if (filterMode === 'posts')  return dedup(posts);
    if (filterMode === 'images') return dedup(submissions.filter(s => s.game_type === 'arts'));
    if (filterMode === 'videos') return dedup(submissions.filter(s => s.game_type === 'performance' || s.game_type === 'artisanat'));
    if (filterMode === 'music')  return dedup(submissions.filter(s => s.game_type === 'music'));
    const merged = [...posts, ...submissions].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return dedup(merged);
  })();

  const allVideoItems: VideoFeedItem[] = displayedItems
    .filter((item): item is Submission =>
      item.item_type === 'submission' && (item.game_type === 'performance' || item.game_type === 'artisanat')
    )
    .flatMap(sub => (sub.media ?? []).map(m => ({
      mediaId: m.id, videoUrl: m.url, description: m.description ?? null,
      author: sub.author, run_number: sub.run_number, run_title: sub.run_title,
      session_id: sub.session_id, total_votes: sub.total_votes, user_voted: sub.user_voted,
      game_type: sub.game_type, run_id: sub.run_id, user_id: sub.user_id,
    })));

  const allAudioItems: AudioFeedItem[] = displayedItems
    .filter((item): item is Submission =>
      item.item_type === 'submission' && item.game_type === 'music'
    )
    .flatMap(sub => (sub.media ?? []).map(m => ({
      mediaId: m.id, audioUrl: m.url, description: m.description ?? null,
      author: sub.author, run_number: sub.run_number, run_title: sub.run_title,
      session_id: sub.session_id, total_votes: sub.total_votes, user_voted: sub.user_voted,
      game_type: sub.game_type, run_id: sub.run_id, user_id: sub.user_id,
    })));

  const isLoadingMore = loadingPosts || loadingSubs;
  const hasMoreAny    =
    (filterMode !== 'images' && filterMode !== 'videos' && filterMode !== 'music' && postsHasMore) ||
    (filterMode !== 'posts' && subsHasMore);

  const filterLabel: Record<FilterMode, string> = {
    all: t('actuScreen.filters.all'), posts: t('actuScreen.filters.posts'), images: t('actuScreen.filters.images'),
    videos: t('actuScreen.filters.videos'), music: t('actuScreen.filters.music'),
  };

  const visibilitiesAllowed: Visibility[] = isPro
    ? ['public', 'friends', 'private', 'pro']
    : ['public', 'friends', 'private'];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={styles.container}>

        {/* ── BANNER HORS-LIGNE ────────────────────────────────────────── */}
        {isOnline === false && (
          <View style={[styles.offlineBanner, { top: measuredHeaderHeight }]}>
            <Ionicons name="cloud-offline-outline" size={16} color="#fff" />
            <Text style={styles.offlineBannerText}>{t('actuScreen.offlineBanner')}</Text>
          </View>
        )}

        {/* ── TOAST ───────────────────────────────────────────────────── */}
        {toast && (
          <View style={[
            styles.toast,
            styles[`toast_${toast.type}` as keyof typeof styles] as any,
            // Si le banner offline est affiché, décaler le toast vers le bas
            isOnline === false && { top: Platform.OS === 'ios' ? 90 : 50 },
          ]}>
            <Ionicons
              name={toast.type === 'success' ? 'checkmark-circle' : toast.type === 'error' ? 'close-circle' : 'information-circle'}
              size={20} color="#fff"
            />
            <Text style={styles.toastText}>{toast.text}</Text>
          </View>
        )}

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <Animated.View
          style={[styles.headerContainer, { transform: [{ translateY: headerAnim }] }]}
          onLayout={e => {
            const h = e.nativeEvent.layout.height;
            if (h > 0 && Math.abs(h - measuredHeaderHeightRef.current) > 1) {
              measuredHeaderHeightRef.current = h;
              setMeasuredHeaderHeight(h);
            }
          }}
        >
          <LinearGradient colors={['#8A2BE2', '#4B0082']} style={styles.header}>
            <View style={styles.headerTop}>
              <HarmoniaLogo size={26} showText={true} />
              <TouchableOpacity
                style={styles.balanceBox}
                onPress={() => {
                  if (!isOnlineRef.current) { showOfflineToast(); return; }
                  if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  router.push('/recharge');
                }}
                activeOpacity={0.8}
              >
                <Ionicons name="wallet-outline" size={14} color="#FFD700" />
                <Text style={styles.balanceText}>{userProfile?.solde_cfa?.toLocaleString() || '0'} CFA</Text>
                <Ionicons name="add-circle-outline" size={13} color="#FFD700" />
              </TouchableOpacity>
            </View>
            <View style={styles.headerActions}>
              <TouchableOpacity
                style={styles.filterBtn}
                onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); setShowFilter(true); }}
              >
                <Ionicons name="filter" size={16} color="#FFD700" />
                <Text style={styles.filterBtnText}>{filterLabel[filterMode]}</Text>
              </TouchableOpacity>
              <View style={styles.actionsBtns}>
                <TouchableOpacity onPress={handleManualRefresh} disabled={isRefreshing}>
                  <Ionicons name="refresh-outline" size={22} color={isRefreshing ? 'rgba(255,255,255,0.4)' : '#fff'} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => {
                  if (!isOnlineRef.current) { showOfflineToast(); return; }
                  if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  setShowSearchModal(true);
                }}>
                  <Ionicons name="search-outline" size={22} color="#fff" />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => {
                  if (!isOnlineRef.current) { showOfflineToast(); return; }
                  if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                  setShowCreatePost(true);
                }}>
                  <LinearGradient colors={['#FFD700', '#FF0080']} start={{ x:0,y:0 }} end={{ x:1,y:1 }} style={styles.createBtnGrad}>
                    <Ionicons name="add" size={18} color="#fff" />
                  </LinearGradient>
                </TouchableOpacity>
                {Platform.OS === 'web' && showPushBtn && !pushDone && (
                  <TouchableOpacity onPress={handleEnablePush} style={styles.pushEnableBtn}>
                    <Ionicons name="notifications-circle" size={24} color="#FFD700" />
                    <View style={styles.pushEnableDot} />
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => {
                  if (!isOnlineRef.current) { showOfflineToast(); return; }
                  if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  router.push('/notifications');
                }}>
                  <Ionicons name="notifications-outline" size={22} color="#fff" />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => {
                  if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  setShowSavedPostsModal(true);
                }}>
                  <Ionicons name="arrow-down-circle-outline" size={22} color="#fff" />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => {
                  if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  setShowLogoutModal(true);
                }}>
                  <Ionicons name="log-out-outline" size={22} color="#FFD700" />
                </TouchableOpacity>
              </View>
            </View>
          </LinearGradient>
        </Animated.View>

        {!headerVisible && (
          <View style={styles.doubleTapHint}>
            <Ionicons name="chevron-down-outline" size={16} color="#8A2BE2" />
            <Text style={styles.doubleTapText}>{t('actuScreen.doubleTapToShow')}</Text>
          </View>
        )}

        {/* ── SCROLL ──────────────────────────────────────────────────── */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: measuredHeaderHeight + 20 },
            // Pousser le contenu sous le banner offline si présent
            isOnline === false && { paddingTop: measuredHeaderHeight + 56 },
          ]}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              // Désactiver le pull-to-refresh si hors ligne
              enabled={isOnline !== false}
            />
          }
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          onTouchEnd={handleDoubleTap}
          onLayout={e => { scrollHeight.current = e.nativeEvent.layout.height; }}
        >
          {displayedItems.length === 0 && !isLoadingMore ? (
            <View style={styles.emptyBox}>
              <Ionicons
                name={isOnline === false ? 'cloud-offline-outline' : 'people-outline'}
                size={64}
                color="#C4B5FD"
              />
              <Text style={styles.emptyText}>
                {isOnline === false
                  ? t('actuScreen.empty.offlineTitle')
                  : t('actuScreen.empty.emptyFeedTitle')}
              </Text>
              <Text style={styles.emptySub}>
                {isOnline === false
                  ? t('actuScreen.empty.offlineSubtitle')
                  : t('actuScreen.empty.emptyFeedSubtitle')}
              </Text>
            </View>
          ) : (
            <View style={styles.feedList}>
              {displayedItems.map((item, index) =>
                item.item_type === 'post' ? (
                  // Wrapper onLayout pour le view tracking
                  <View
                    key={`post-${item.id}`}
                    onLayout={e => {
                      // top = position absolue dans le ScrollView
                      // Pour les éléments dans un ScrollView, on accumule les offsets
                      const { y, height } = e.nativeEvent.layout;
                      handlePostLayout(item.id, y, height);
                    }}
                  >
                    <PostCard
                      post={item} userId={userId} isAdmin={isAdmin}
                      onLike={handleLike} onShare={handleShare} onSave={handleSave}
                      onReport={handleReportPost} onAdminStrike={handleAdminStrikeAction}
                      onOpenComments={handleOpenComments}
                      onOpenLikers={handleOpenLikers}
                      onOpenSharers={handleOpenSharers}
                      onOpenEdit={handleOpenEdit}
                      onLongPress={handleLongPress}
                      onAuthorPress={handleAuthorPress}
                    />
                  </View>
                ) : (
                  <SubmissionCard
                    key={`sub-${item.id}`}
                    sub={item} onVote={handleVote}
                    allVideoItems={allVideoItems} allAudioItems={allAudioItems}
                    openVideoModal={openVideoModal} openAudioModal={openAudioModal}
                    onAuthorPress={handleAuthorPress}
                  />
                )
              )}
              {isLoadingMore && (
                <View style={styles.loadMoreIndicator}>
                  <ActivityIndicator size="small" color="#8A2BE2" />
                  <Text style={styles.loadMoreText}>{t('actuScreen.loading')}</Text>
                </View>
              )}
              {!isLoadingMore && !hasMoreAny && displayedItems.length > 0 && (
                <View style={styles.endOfFeed}>
                  <View style={styles.endOfFeedLine} />
                  <Text style={styles.endOfFeedText}>{t('actuScreen.upToDate')}</Text>
                  <View style={styles.endOfFeedLine} />
                </View>
              )}
            </View>
          )}
        </ScrollView>

        {/* ── MODAL CONFIRM ───────────────────────────────────────────── */}
        <Modal visible={!!confirmConfig?.visible} transparent animationType="fade">
          <View style={styles.modalOverlay}>
            <View style={styles.confirmModalContent}>
              <Text style={styles.confirmTitle}>{confirmConfig?.title}</Text>
              <Text style={styles.confirmMessage}>{confirmConfig?.message}</Text>
              <View style={styles.confirmActions}>
                <TouchableOpacity style={styles.confirmCancelBtn} onPress={() => setConfirmConfig(null)}>
                  <Text style={styles.confirmCancelText}>{confirmConfig?.cancelText}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.confirmActionBtn, confirmConfig?.isDestructive && { backgroundColor: '#EF4444' }]}
                  onPress={() => { if (confirmConfig?.onConfirm) confirmConfig.onConfirm(); setConfirmConfig(null); }}
                >
                  <Text style={styles.confirmActionText}>{confirmConfig?.confirmText}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* ── MODAL FRAPPE ADMIN ───────────────────────────────────────── */}
        <Modal visible={!!adminStrikePostId} transparent animationType="fade">
          <View style={styles.modalOverlay}>
            <View style={styles.adminModalContent}>
              <View style={styles.adminModalHeader}>
                <Ionicons name="shield-half" size={24} color="#E74C3C" />
                <Text style={styles.adminModalTitle}>{t('actuScreen.adminStrike.title')}</Text>
              </View>
              <Text style={styles.adminModalDesc}>{t('actuScreen.adminStrike.selectSanction')}</Text>
              <TouchableOpacity style={styles.adminOptionBtn} onPress={() => { executeAdminStrike(adminStrikePostId!, 'pending'); setAdminStrikePostId(null); }}>
                <Ionicons name="time-outline" size={20} color="#F59E0B" />
                <Text style={styles.adminOptionText}>{t('actuScreen.adminStrike.pending')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.adminOptionBtn, { borderBottomWidth: 0 }]} onPress={() => { executeAdminStrike(adminStrikePostId!, 'active'); setAdminStrikePostId(null); }}>
                <Ionicons name="lock-closed-outline" size={20} color="#EF4444" />
                <Text style={[styles.adminOptionText, { color: '#EF4444' }]}>{t('actuScreen.adminStrike.lock')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.adminCancelBtn} onPress={() => setAdminStrikePostId(null)}>
                <Text style={styles.adminCancelText}>{t('actuScreen.adminStrike.cancel')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        {/* ── MODAL FILTRE ─────────────────────────────────────────────── */}
        <Modal visible={showFilter} transparent animationType="fade" onRequestClose={() => setShowFilter(false)}>
          <TouchableOpacity style={styles.filterOverlay} activeOpacity={1} onPress={() => setShowFilter(false)}>
            <View style={styles.filterMenu}>
              <Text style={styles.filterMenuTitle}>{t('actuScreen.filterMenu.title')}</Text>
              {([
                { key: 'all',    label: t('actuScreen.filters.all'),    icon: 'apps-outline' },
                { key: 'posts',  label: t('actuScreen.filters.posts'),  icon: 'document-text-outline' },
                { key: 'images', label: t('actuScreen.filters.images'), icon: 'images-outline' },
                { key: 'videos', label: t('actuScreen.filters.videos'), icon: 'videocam-outline' },
                { key: 'music',  label: t('actuScreen.filters.music'),  icon: 'musical-notes-outline' },
              ] as { key: FilterMode; label: string; icon: string }[]).map(opt => (
                <TouchableOpacity
                  key={opt.key}
                  style={[styles.filterOption, filterMode === opt.key && styles.filterOptionActive]}
                  onPress={() => applyFilter(opt.key)}
                >
                  <Ionicons name={opt.icon as any} size={20} color={filterMode === opt.key ? '#8A2BE2' : '#666'} />
                  <Text style={[styles.filterOptionText, filterMode === opt.key && styles.filterOptionTextActive]}>{opt.label}</Text>
                  {filterMode === opt.key && <Ionicons name="checkmark" size={18} color="#8A2BE2" />}
                </TouchableOpacity>
              ))}
            </View>
          </TouchableOpacity>
        </Modal>

        {/* ── MODAL CRÉER POST ─────────────────────────────────────────── */}
        <Modal visible={showCreatePost} transparent animationType="slide" onRequestClose={() => setShowCreatePost(false)}>
          <KeyboardAvoidingView
            style={styles.cpOverlay}
            behavior={
              Platform.OS === 'ios'     ? 'padding'
              : Platform.OS === 'android' ? 'height'
              : undefined
            }
            keyboardVerticalOffset={Platform.OS === 'android' ? 80 : 0}
          >
            <View style={styles.cpCard}>
              <View style={styles.cpHeader}>
                <Text style={styles.cpTitle}>{t('actuScreen.createPost.title')}</Text>
                <TouchableOpacity onPress={() => { setShowCreatePost(false); setPostImageUrl(null); setPostVideoUrl(null); setPostContent(''); }}>
                  <Ionicons name="close" size={24} color="#666" />
                </TouchableOpacity>
              </View>

              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={styles.cpScrollContent}
              >
                <View style={styles.cpAuthorRow}>
                  <SafeImage
                    uri={myProfile?.avatar_url}
                    style={styles.cpAvatar}
                    resizeMode="cover"
                    fallback={
                      <View style={[styles.cpAvatar, styles.cpAvatarFallback]}>
                        <Text style={styles.cpAvatarText}>{myProfile ? getInitials(myProfile.nom, myProfile.prenom) : '?'}</Text>
                      </View>
                    }
                  />
                  <View>
                    <Text style={styles.cpAuthorName}>{myProfile ? `${myProfile.prenom} ${myProfile.nom}` : '…'}</Text>
                    {isPro && <View style={styles.cpProBadge}><Text style={styles.cpProBadgeText}>⭐ PRO</Text></View>}
                    <View style={styles.cpVisibilityRow}>
                      {visibilitiesAllowed.map(v => (
                        <TouchableOpacity
                          key={v}
                          style={[styles.cpVisibilityBtn, postVisibility === v && styles.cpVisibilityBtnActive]}
                          onPress={() => setPostVisibility(v)}
                        >
                          <Ionicons
                            name={v === 'public' ? 'globe-outline' : v === 'friends' ? 'people-outline' : v === 'pro' ? 'star' : 'lock-closed-outline'}
                            size={12}
                            color={postVisibility === v ? '#fff' : '#666'}
                          />
                          <Text style={[styles.cpVisibilityText, postVisibility === v && styles.cpVisibilityTextActive]}>
                            {v === 'public' ? t('actuScreen.visibility.public') : v === 'friends' ? t('actuScreen.visibility.friends') : v === 'pro' ? t('actuScreen.visibility.pro') : t('actuScreen.visibility.private')}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                </View>

                <TextInput
                  style={styles.cpInput}
                  placeholder={t('actuScreen.createPost.placeholder')}
                  placeholderTextColor="#bbb"
                  value={postContent}
                  onChangeText={setPostContent}
                  multiline maxLength={1000} autoFocus
                />
                <Text style={styles.cpCharCount}>{postContent.length}/1000</Text>

                {postImageUrl && (
                  <View style={styles.cpMediaContainer}>
                    <SafeImage uri={postImageUrl} style={styles.cpMediaPreview} resizeMode="cover" />
                    <TouchableOpacity style={styles.cpMediaRemove} onPress={() => setPostImageUrl(null)}>
                      <Ionicons name="close-circle" size={24} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                )}
                {postVideoUrl && (
                  <View style={styles.cpMediaContainer}>
                    <View style={[styles.cpMediaPreview, styles.cpVideoPreviewBg]}>
                      <Ionicons name="play-circle" size={40} color="rgba(255,255,255,0.9)" />
                      <Text style={styles.cpVideoPreviewLabel}>{t('actuScreen.createPost.videoReady')}</Text>
                    </View>
                    <TouchableOpacity style={styles.cpMediaRemove} onPress={() => setPostVideoUrl(null)}>
                      <Ionicons name="close-circle" size={24} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                )}
                {isPro && !postImageUrl && !postVideoUrl && (
                  <View style={styles.cpMediaButtons}>
                    <TouchableOpacity style={styles.cpMediaBtn} onPress={() => handlePickMedia('image')} disabled={uploadingMedia}>
                      {uploadingMedia ? <ActivityIndicator size="small" color="#8A2BE2" />
                        : <><Ionicons name="image-outline" size={20} color="#8A2BE2" /><Text style={styles.cpMediaBtnText}>{t('actuScreen.createPost.photo')}</Text></>}
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.cpMediaBtn} onPress={() => handlePickMedia('video')} disabled={uploadingMedia}>
                      {uploadingMedia ? <ActivityIndicator size="small" color="#8A2BE2" />
                        : <><Ionicons name="videocam-outline" size={20} color="#8A2BE2" /><Text style={styles.cpMediaBtnText}>{t('actuScreen.createPost.video')}</Text></>}
                    </TouchableOpacity>
                  </View>
                )}

                <TouchableOpacity
                  style={[styles.cpPublishBtn, !postContent.trim() && styles.cpPublishBtnDisabled]}
                  onPress={handleCreatePost}
                  disabled={creatingPost || !postContent.trim()}
                  activeOpacity={0.8}
                >
                  {creatingPost
                    ? <ActivityIndicator size="small" color="#fff" />
                    : <><Ionicons name="send" size={18} color="#fff" /><Text style={styles.cpPublishBtnText}>{t('actuScreen.createPost.publish')}</Text></>
                  }
                </TouchableOpacity>
              </ScrollView>
            </View>
          </KeyboardAvoidingView>
        </Modal>

        {/* ── MODAL DÉTAILS POST ───────────────────────────────────────── */}
        {selectedPost && (
          <Modal visible={!!selectedPost} transparent animationType="fade" onRequestClose={() => setSelectedPost(null)}>
            <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setSelectedPost(null)}>
              <View style={styles.modalContent}>
                <Text style={styles.modalTitle}>{t('actuScreen.postDetails.title')}</Text>
                <View style={styles.modalInfo}><Text style={styles.modalLabel}>{t('actuScreen.postDetails.author')}</Text><Text style={styles.modalValue}>{selectedPost.author.prenom} {selectedPost.author.nom}</Text></View>
                <View style={styles.modalInfo}><Text style={styles.modalLabel}>{t('actuScreen.postDetails.published')}</Text><Text style={styles.modalValue}>{formatTimeAgo(selectedPost.created_at)}</Text></View>
                <View style={styles.modalInfo}><Text style={styles.modalLabel}>{t('actuScreen.postDetails.reactions')}</Text><Text style={styles.modalValue}>👍 {selectedPost.reactions.likes} · 💬 {selectedPost.reactions.comments} · 🔄 {selectedPost.reactions.shares}</Text></View>
                <TouchableOpacity style={styles.modalBtn} onPress={() => setSelectedPost(null)}>
                  <Text style={styles.modalBtnText}>{t('actuScreen.postDetails.close')}</Text>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          </Modal>
        )}

        {/* ── Modaux existants ─────────────────────────────────────────── */}
        {showUserProfileModal && selectedAuthorId && (
          <UserProfileView
            userId={selectedAuthorId} viewerId={userId} accessToken={accessToken}
            asModal onClose={() => { setShowUserProfileModal(false); setSelectedAuthorId(null); }}
          />
        )}
        <CommentsModal
          visible={showCommentsModal} postId={selectedPostForComments} userId={userId}
          onClose={() => { setShowCommentsModal(false); setSelectedPostForComments(null); }}
          onCommentAdded={() => { loadPosts(true); }}
        />
        <LikersModal
          visible={showLikersModal} postId={selectedPostForLikers}
          onClose={() => { setShowLikersModal(false); setSelectedPostForLikers(null); }}
        />
        <SharersModal
          visible={showSharersModal}
          postId={selectedPostForSharers}
          onClose={() => { setShowSharersModal(false); setSelectedPostForSharers(null); }}
        />
        <EditPostModal
          visible={showEditModal} postId={selectedPostForEdit?.id || null}
          userId={userId} initialContent={selectedPostForEdit?.content || ''}
          onClose={() => { setShowEditModal(false); setSelectedPostForEdit(null); }}
          onPostUpdated={async () => { await loadPosts(true); }}
        />
        <SavedPostsModal
          visible={showSavedPostsModal} userId={userId}
          onClose={() => setShowSavedPostsModal(false)}
          onCommentPress={(postId) => {
            setShowSavedPostsModal(false);
            setSelectedPostForComments(postId);
            setShowCommentsModal(true);
          }}
        />
        <SearchModal
          visible={showSearchModal} userId={userId}
          onClose={() => setShowSearchModal(false)}
          onPostPress={(postId) => { setSelectedPostForComments(postId); setShowCommentsModal(true); }}
        />

        {/*
          LogoutModal :
          - `profile` et `refreshToken` sont désormais transmis pour que le
            modal puisse sauvegarder le compte (saveAccount) si l'utilisateur
            choisit de mémoriser.
          - `onLogoutSuccess` reçoit `wantsToRemember` et est le SEUL endroit
            qui déclenche router.replace (voir handleLogoutSuccess ci-dessus).
        */}
        <LogoutModal
          visible={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          profile={myProfile ? {
            id: myProfile.id,
            nom: myProfile.nom,
            prenom: myProfile.prenom,
            avatar_url: myProfile.avatar_url,
          } : null}
          refreshToken={refreshTokenRef.current}
          onLogoutSuccess={handleLogoutSuccess}
        />

        <TikTokVideoModal
          visible={videoModal} items={videoItems} startIndex={videoStartIndex}
          onClose={() => setVideoModal(false)} onVote={handleVote}
        />
        <AudioFeedModal
          visible={audioModal} items={audioItems} startIndex={audioStartIndex}
          onClose={() => setAudioModal(false)} onVote={handleVote}
        />
        <PushPromptModal
          visible={showPushPrompt} onAccept={handleEnablePush} onDismiss={handleDismissPrompt}
        />

        {/* LimitNotification */}
        <LimitNotification
          visible={limitNotif}
          message={limitNotifMessage}
          isPro={isPro}
          onDismiss={() => setLimitNotif(false)}
        />

      </View>
    </KeyboardAvoidingView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:       { flex: 1, backgroundColor: '#F0F2F5' },
  headerContainer: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000 },
  header:          { paddingTop: HEADER_PADDING_TOP, paddingBottom: 8, paddingHorizontal: 14 },
  headerTop:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },

  // ── Banner offline ───────────────────────────────────────────────────────────
  offlineBanner: {
    position: 'absolute',
    left: 0, right: 0,
    zIndex: 999,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EF4444',
    paddingVertical: 8,
    paddingHorizontal: 16,
    gap: 8,
  },
  offlineBannerText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },

  balanceBox:    { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12, gap: 5 },
  balanceText:   { color: '#FFD700', fontSize: 12, fontWeight: '800' },

  headerActions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  filterBtn:     { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.15)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10 },
  filterBtnText: { color: '#FFD700', fontSize: 12, fontWeight: '700' },
  actionsBtns:   { flexDirection: 'row', alignItems: 'center', gap: 14 },
  pushEnableBtn: { position: 'relative' },
  pushEnableDot: { position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: 4, backgroundColor: '#FF0080', borderWidth: 1.5, borderColor: '#4B0082' },
  createBtnGrad: { width: 30, height: 30, borderRadius: 15, justifyContent: 'center', alignItems: 'center' },

  doubleTapHint: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0E6FF', paddingVertical: 6, gap: 4 },
  doubleTapText: { fontSize: 11, color: '#8A2BE2', fontWeight: '600' },
  scrollView:    { flex: 1 },
  scrollContent: { paddingTop: HEADER_HEIGHT + 20 },
  feedList:      { paddingVertical: 8, paddingBottom: 100 },
  emptyBox:      { alignItems: 'center', paddingVertical: 60 },
  emptyText:     { fontSize: 16, color: '#999', marginTop: 12 },
  emptySub:      { fontSize: 12, color: '#CCC', marginTop: 5, textAlign: 'center', paddingHorizontal: 30 },

  loadMoreIndicator: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 20, gap: 10 },
  loadMoreText:      { fontSize: 13, color: '#8A2BE2', fontWeight: '600' },
  endOfFeed:         { flexDirection: 'row', alignItems: 'center', paddingVertical: 24, paddingHorizontal: 20, gap: 12 },
  endOfFeedLine:     { flex: 1, height: 1, backgroundColor: '#E0D0FF' },
  endOfFeedText:     { fontSize: 12, color: '#B39DDB', fontWeight: '600' },

  toast:         { position: 'absolute', top: Platform.OS === 'ios' ? 55 : 15, left: 20, right: 20, zIndex: 2000, flexDirection: 'row', alignItems: 'center', padding: 14, borderRadius: 12, gap: 10 },
  toast_success: { backgroundColor: '#10B981' },
  toast_error:   { backgroundColor: '#EF4444' },
  toast_info:    { backgroundColor: '#3B82F6' },
  toastText:     { flex: 1, color: '#fff', fontSize: 14, fontWeight: '600' },

  modalOverlay:        { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' },
  modalContent:        { backgroundColor: '#fff', borderRadius: 20, padding: 25, width: width * 0.85, maxWidth: 400 },
  modalTitle:          { fontSize: 18, fontWeight: 'bold', color: '#333', marginBottom: 20, textAlign: 'center' },
  modalInfo:           { marginBottom: 12 },
  modalLabel:          { fontSize: 12, color: '#999', marginBottom: 3 },
  modalValue:          { fontSize: 14, color: '#333', fontWeight: '600' },
  modalBtn:            { backgroundColor: '#8A2BE2', padding: 12, borderRadius: 10, alignItems: 'center', marginTop: 10 },
  modalBtnText:        { color: '#fff', fontSize: 14, fontWeight: 'bold' },

  confirmModalContent: { backgroundColor: '#fff', borderRadius: 16, padding: 24, width: width * 0.85, maxWidth: 350 },
  confirmTitle:        { fontSize: 18, fontWeight: 'bold', color: '#1A1A1A', marginBottom: 10 },
  confirmMessage:      { fontSize: 15, color: '#666', lineHeight: 22, marginBottom: 24 },
  confirmActions:      { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
  confirmCancelBtn:    { paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8 },
  confirmCancelText:   { color: '#666', fontWeight: '600', fontSize: 15 },
  confirmActionBtn:    { backgroundColor: '#8A2BE2', paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8 },
  confirmActionText:   { color: '#FFF', fontWeight: '600', fontSize: 15 },

  adminModalContent: { backgroundColor: '#fff', borderRadius: 16, width: width * 0.85, maxWidth: 350, overflow: 'hidden' },
  adminModalHeader:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 20, backgroundColor: '#FEF2F2' },
  adminModalTitle:   { fontSize: 18, fontWeight: 'bold', color: '#E74C3C' },
  adminModalDesc:    { fontSize: 14, color: '#666', textAlign: 'center', padding: 16 },
  adminOptionBtn:    { flexDirection: 'row', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#F0F0F0', gap: 12 },
  adminOptionText:   { fontSize: 15, fontWeight: '600', color: '#333' },
  adminCancelBtn:    { padding: 16, alignItems: 'center', backgroundColor: '#F9FAFB' },
  adminCancelText:   { color: '#666', fontWeight: 'bold', fontSize: 15 },

  filterOverlay:          { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-start', alignItems: 'flex-start', paddingTop: FILTER_OVERLAY_TOP, paddingLeft: 14 },
  filterMenu:             { backgroundColor: '#FFF', borderRadius: 16, padding: 8, minWidth: 200, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 10, elevation: 8 },
  filterMenuTitle:        { color: '#999', fontSize: 11, fontWeight: '700', paddingHorizontal: 14, paddingVertical: 8, textTransform: 'uppercase' },
  filterOption:           { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 14, paddingVertical: 12, borderRadius: 10 },
  filterOptionActive:     { backgroundColor: '#F0E6FF' },
  filterOptionText:       { flex: 1, fontSize: 14, color: '#333', fontWeight: '600' },
  filterOptionTextActive: { color: '#8A2BE2' },

  // ── Create post modal ────────────────────────────────────────────────────────
  cpOverlay:              { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  cpCard:                 { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, paddingTop: 24, paddingHorizontal: 24, paddingBottom: Platform.OS === 'ios' ? 40 : 24, maxHeight: '90%' },
  cpScrollContent:        { paddingBottom: 8 },
  cpHeader:               { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cpTitle:                { fontSize: 18, fontWeight: 'bold', color: '#2D0072' },
  cpAuthorRow:            { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  cpAvatar:               { width: 44, height: 44, borderRadius: 22 },
  cpAvatarFallback:       { backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  cpAvatarText:           { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  cpAuthorName:           { fontSize: 15, fontWeight: '700', color: '#222', marginBottom: 4 },
  cpProBadge:             { backgroundColor: '#FFF8E1', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2, marginBottom: 6, alignSelf: 'flex-start' },
  cpProBadgeText:         { color: '#F59E0B', fontSize: 11, fontWeight: '700' },
  cpVisibilityRow:        { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  cpVisibilityBtn:        { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12, backgroundColor: '#F5F5F5', borderWidth: 1, borderColor: '#E0E0E0' },
  cpVisibilityBtnActive:  { backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' },
  cpVisibilityText:       { fontSize: 11, color: '#666', fontWeight: '600' },
  cpVisibilityTextActive: { color: '#fff' },
  cpInput:                { fontSize: 16, color: '#333', minHeight: 100, textAlignVertical: 'top', paddingVertical: 12, marginBottom: 8 },
  cpCharCount:            { fontSize: 12, color: '#bbb', textAlign: 'right', marginBottom: 12 },
  cpMediaButtons:         { flexDirection: 'row', gap: 10, marginBottom: 16 },
  cpMediaBtn:             { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 8, backgroundColor: '#F0E6FF', borderRadius: 12 },
  cpMediaBtnText:         { fontSize: 13, color: '#8A2BE2', fontWeight: '600' },
  cpMediaContainer:       { position: 'relative', marginBottom: 12 },
  cpMediaPreview:         { width: '100%', height: 180, borderRadius: 12, backgroundColor: '#F0F0F0' },
  cpVideoPreviewBg:       { backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', gap: 8 },
  cpVideoPreviewLabel:    { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  cpMediaRemove:          { position: 'absolute', top: 8, right: 8 },
  cpPublishBtn:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#8A2BE2', borderRadius: 14, paddingVertical: 16, marginTop: 8 },
  cpPublishBtnDisabled:   { backgroundColor: '#C4B5FD' },
  cpPublishBtnText:       { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});
